import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { ShoppingCart, Trash2, Hotel, Calendar, Info, Clock, RefreshCw, CheckCheck } from "lucide-react";
import { useCart } from "@/hooks/useCart";
import { Badge } from "@/components/ui/badge";
import { Link } from "@/lib/react-router-dom-shim";
import { format, parse } from "date-fns";
import { es, ptBR, enUS } from "date-fns/locale";
import { useState, useMemo, useCallback } from "react";
import { getBoardTypeIcon, getBoardTypeColor } from "@/utils/boardTypeIcons";
import { translateBoardType } from "@/utils/boardTypeTranslations";
import { Loader2, AlertCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ValidationChangesDialog } from "@/components/ValidationChangesDialog";
import { CartExpirationDialog } from "@/components/CartExpirationDialog";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import { usePrice } from "@/hooks/usePrice";
import { Skeleton } from "@/components/ui/skeleton";
import { TranslatedText } from "@/components/TranslatedText";
import { useCartExpiration } from "@/hooks/useCartExpiration";

export const CartSheet = () => {
  const { t, i18n } = useTranslation();
  const { format: formatPrice, isConverting } = usePrice();
  const dateLocale = i18n.language === 'pt-BR' ? ptBR : i18n.language === 'en' ? enUS : es;
  const { items, itemCount, total, removeItem, hasUnvalidatedItems, revalidateItem } = useCart();
  const { toast } = useToast();
  
  const [changesDialogOpen, setChangesDialogOpen] = useState(false);
  const [selectedItemForChanges, setSelectedItemForChanges] = useState<any>(null);
  const [parsedChanges, setParsedChanges] = useState<any[]>([]);
  const [isUpdatingAll, setIsUpdatingAll] = useState(false);

  // Use the expiration hook
  const {
    minTimeRemaining,
    expiredItems,
    showExpirationDialog,
    setShowExpirationDialog,
    resetItemExpiration,
    dismissExpiredItems,
  } = useCartExpiration(items);

  // Convert ms to seconds for display
  const timeLeftSeconds = Math.floor(minTimeRemaining / 1000);

  // Get items with changes detected
  const itemsWithChanges = useMemo(() => 
    items.filter(item => item.validation_status === 'changes_found'),
    [items]
  );

  const propertyTaxesTotal = useMemo(() => {
    return items.reduce((sum, item) => {
      const details = item.booking_details as any;
      const itemTaxes = details?.propertyTaxesEur || 0;
      const quantity = (item as any).quantity ?? 1;
      return sum + itemTaxes * quantity;
    }, 0);
  }, [items]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleShowChanges = (item: any) => {
    try {
      const validationData = JSON.parse(item.validation_error || '{}');
      const changes = validationData.changes || [];
      setParsedChanges(changes);
      setSelectedItemForChanges(item);
      setChangesDialogOpen(true);
    } catch (error) {
      console.error('Error parsing changes:', error);
    }
  };

  const handleConfirmChanges = async () => {
    if (selectedItemForChanges) {
      try {
        // Parse validation data to get new values
        const validationData = JSON.parse(selectedItemForChanges.validation_error || '{}');
        const newData = validationData.validatedData;
        
        if (newData) {
          // Update cart item with new validated data
          await supabase
            .from('cart_items')
            .update({ 
              validation_status: 'validated',
              validation_error: null,
              price_eur: newData.price_eur,
              booking_details: newData.booking_details
            })
            .eq('id', selectedItemForChanges.id);
        } else {
          // If no validated data, just mark as validated
          await supabase
            .from('cart_items')
            .update({ 
              validation_status: 'validated',
              validation_error: null
            })
            .eq('id', selectedItemForChanges.id);
        }
        
        // Reset expiration timer for this item
        resetItemExpiration(selectedItemForChanges.id);
        
        // Refresh cart to show updated data
        window.location.reload();
      } catch (error) {
        console.error('Error updating cart item:', error);
      }
    }
    setChangesDialogOpen(false);
    setSelectedItemForChanges(null);
  };

  const handleRemoveChangedItem = () => {
    if (selectedItemForChanges) {
      removeItem(selectedItemForChanges.id);
    }
    setChangesDialogOpen(false);
    setSelectedItemForChanges(null);
  };

  const handleUpdateAll = async () => {
    if (itemsWithChanges.length === 0) return;
    
    setIsUpdatingAll(true);
    try {
      // Update all items with changes
      const updatePromises = itemsWithChanges.map(async (item) => {
        try {
          const validationData = JSON.parse(item.validation_error || '{}');
          const newData = validationData.validatedData;
          
          if (newData) {
            await supabase
              .from('cart_items')
              .update({ 
                validation_status: 'validated',
                validation_error: null,
                price_eur: newData.price_eur,
                booking_details: newData.booking_details
              })
              .eq('id', item.id);
            
            // Reset expiration timer for this item
            resetItemExpiration(item.id);
          }
        } catch (error) {
          console.error(`Error updating item ${item.id}:`, error);
        }
      });

      await Promise.all(updatePromises);
      
      toast({
        title: t('cart.updated'),
        description: t('cart.updatedDescription', { count: itemsWithChanges.length }),
      });
      
      // Refresh cart
      window.location.reload();
    } catch (error) {
      console.error('Error updating all items:', error);
      toast({
        title: t('common.error'),
        description: t('cart.updateError'),
        variant: "destructive",
      });
    } finally {
      setIsUpdatingAll(false);
    }
  };

  // Handle expired items revalidation
  const handleRevalidateExpired = useCallback(async (itemIds: string[]) => {
    for (const itemId of itemIds) {
      await revalidateItem(itemId);
      resetItemExpiration(itemId);
    }
    dismissExpiredItems(itemIds);
  }, [revalidateItem, resetItemExpiration, dismissExpiredItems]);

  // Handle expired items removal
  const handleRemoveExpired = useCallback((itemIds: string[]) => {
    itemIds.forEach(id => removeItem(id));
    dismissExpiredItems(itemIds);
  }, [removeItem, dismissExpiredItems]);

  // Determine timer color based on remaining time
  const getTimerColor = () => {
    if (timeLeftSeconds <= 0) return 'text-destructive';
    if (timeLeftSeconds <= 180) return 'text-destructive animate-pulse'; // 3 minutes
    if (timeLeftSeconds <= 300) return 'text-amber-600 dark:text-amber-400'; // 5 minutes
    return 'text-muted-foreground';
  };

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" aria-label={t('cart.title')}>
          <ShoppingCart className={`h-5 w-5 ${timeLeftSeconds > 0 && timeLeftSeconds <= 180 ? 'animate-pulse' : ''}`} />
          {itemCount > 0 && (
            <Badge 
              variant="destructive" 
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
            >
              {itemCount}
            </Badge>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent className="w-full sm:max-w-lg flex flex-col">
        <SheetHeader className="flex-shrink-0 space-y-2">
          <SheetTitle>{t('cart.title')}</SheetTitle>
          {items.length > 0 && (
            <div className="flex items-center gap-2 text-sm">
              <Clock className={`h-4 w-4 ${getTimerColor()}`} />
              <span className={`font-semibold ${getTimerColor()}`}>
                {t('cart.timeRemaining')}: {formatTime(timeLeftSeconds)}
              </span>
            </div>
          )}
          
          {/* Warning banner when less than 3 minutes */}
          {items.length > 0 && timeLeftSeconds > 0 && timeLeftSeconds <= 180 && (
            <Alert className="border-destructive bg-destructive/10">
              <AlertCircle className="h-4 w-4 text-destructive" />
              <AlertDescription className="text-sm text-destructive">
                {t('cart.expirationWarningDescription')}
              </AlertDescription>
            </Alert>
          )}
          
          {/* Update All Button */}
          {itemsWithChanges.length > 1 && (
            <Alert className="border-amber-500 bg-amber-50 dark:bg-amber-950">
              <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
              <AlertDescription className="flex items-center justify-between">
                <span className="text-sm text-amber-800 dark:text-amber-300">
                  {t('cart.productsWithChanges', { count: itemsWithChanges.length })}
                </span>
                <Button
                  size="sm"
                  variant="default"
                  onClick={handleUpdateAll}
                  disabled={isUpdatingAll}
                  className="ml-2 h-8 shrink-0"
                >
                  {isUpdatingAll ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      {t('cart.updating')}
                    </>
                  ) : (
                    <>
                      <CheckCheck className="h-4 w-4 mr-2" />
                      {t('cart.updateAll')}
                    </>
                  )}
                </Button>
              </AlertDescription>
            </Alert>
          )}
        </SheetHeader>
        
        <div className="flex flex-col h-[calc(100vh-5rem)]">
          {items.length === 0 ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center text-muted-foreground">
                <ShoppingCart className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>{t('cart.empty')}</p>
              </div>
            </div>
          ) : (
            <>
              <div className="flex-1 overflow-auto pr-2 space-y-4" style={{ maxHeight: 'calc(100vh - 16rem)' }}>
                {items.map((item) => {
                  const details = item.booking_details as any;
                  const isHotelOrResort = details?.type === 'hotel' || details?.type === 'resort';
                  const isValidating = item.validation_status === 'validating';
                  const hasValidationError = item.validation_status === 'changes_found' || item.validation_error;
                  
                  return (
                    <div key={item.id} className="border rounded-lg p-4 space-y-3">
                      {/* Validation status banner */}
                      {isValidating && (
                        <Alert className="py-2">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          <AlertDescription className="text-xs">
                            {t('cart.validatingAvailability')}
                          </AlertDescription>
                        </Alert>
                      )}
                      {hasValidationError && (
                        <Alert variant="destructive" className="py-2">
                          <AlertCircle className="h-4 w-4" />
                          <AlertDescription className="text-xs flex items-center justify-between">
                            <span>
                              {item.validation_status === 'changes_found' 
                                ? t('cart.changesDetected')
                                : item.validation_error}
                            </span>
                            <div className="flex gap-2 shrink-0">
                              {item.validation_status === 'changes_found' ? (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="ml-2 h-6 text-xs"
                                  onClick={() => handleShowChanges(item)}
                                >
                                  {t('cart.viewChanges')}
                                </Button>
                              ) : !item.search_token ? (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="ml-2 h-6 text-xs"
                                  onClick={() => removeItem(item.id)}
                                >
                                  {t('common.delete')}
                                </Button>
                              ) : item.validation_status !== 'validating' ? (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="ml-2 h-6 text-xs"
                                  onClick={() => revalidateItem(item.id)}
                                >
                                  <RefreshCw className="h-3 w-3 mr-1" />
                                  {t('cart.retry')}
                                </Button>
                              ) : null}
                            </div>
                          </AlertDescription>
                        </Alert>
                      )}
                      <div className="flex gap-4">
                        {item.product?.images?.[0] && (
                          <img
                            src={item.product.images[0]}
                            alt={item.product.name_es}
                            className="w-20 h-20 object-cover rounded"
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold truncate">
                            {item.product?.name_es || item.product?.name}
                          </h3>
                          <p className="font-semibold mt-1">
                            {formatPrice(item.price_eur)}
                          </p>
                          {details?.propertyTaxesEur && details.propertyTaxesEur > 0 && (
                            <p className="text-xs text-amber-700 dark:text-amber-200 mt-1">
                              + {formatPrice(details.propertyTaxesEur)} {t('hotels.localTaxes')} ({t('hotels.payAtHotel')})
                            </p>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeItem(item.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>

                      {/* Hotel/Resort Information */}
                      {isHotelOrResort && details?.hotelName && (
                        <div className="space-y-2 pt-2 border-t">
                          <div className="flex items-start gap-2">
                            <Hotel className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                            <div className="min-w-0 flex-1">
                              <p className="text-xs font-medium text-muted-foreground">
                                {details.type === 'resort' ? 'Resort' : 'Hotel'}
                              </p>
                              <p className="text-sm">{details.hotelName}</p>
                            </div>
                          </div>

                          {details.checkIn && details.checkOut && (
                            <div className="flex items-start gap-2">
                              <Calendar className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                              <div className="min-w-0 flex-1">
                                <p className="text-xs font-medium text-muted-foreground">{t('cart.dates')}</p>
                                <p className="text-sm">
                                  {format(parse(details.checkIn, 'yyyy-MM-dd', new Date()), 'd MMM', { locale: dateLocale })} - {format(parse(details.checkOut, 'yyyy-MM-dd', new Date()), 'd MMM yyyy', { locale: dateLocale })}
                                </p>
                                {details.numNights && (
                                  <p className="text-xs text-muted-foreground">
                                    {details.numNights} {details.numNights === 1 ? t('common.night') : t('common.nights')}
                                  </p>
                                )}
                              </div>
                            </div>
                          )}

                          {details.boardDescription && (
                            <div className="flex items-start gap-2">
                              {(() => {
                                const translatedBoard = translateBoardType(details.boardDescription, i18n.language);
                                const BoardIcon = getBoardTypeIcon(translatedBoard);
                                const iconColor = getBoardTypeColor(translatedBoard);
                                return <BoardIcon className={`h-4 w-4 mt-0.5 flex-shrink-0 ${iconColor}`} />;
                              })()}
                              <div className="min-w-0 flex-1">
                                <p className="text-xs font-medium text-muted-foreground">{t('hotels.boardType')}</p>
                                <p className="text-sm">{translateBoardType(details.boardDescription, i18n.language)}</p>
                              </div>
                            </div>
                          )}

                          {details.cancellationPolicy && (
                            <div className="flex items-start gap-2">
                              <Info className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                              <div className="min-w-0 flex-1">
                                <p className="text-xs font-medium text-muted-foreground">{t('cart.cancellation')}</p>
                                <p className="text-xs text-muted-foreground line-clamp-2">
                                  <TranslatedText text={details.cancellationPolicy} />
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              
              <div className="border-t pt-4 space-y-4 mt-auto">
                <div className="space-y-2">
                  {propertyTaxesTotal > 0 ? (
                    <>
                      <div className="flex justify-between text-lg font-semibold">
                        <span>{t('checkout.total')}</span>
                        <span>
                          {isConverting ? (
                            <Skeleton className="h-6 w-24 inline-block" />
                          ) : (
                            formatPrice(total)
                          )}
                        </span>
                      </div>
                      <div className="flex justify-between text-xs text-amber-700 dark:text-amber-200">
                        <span className="font-medium">{t('hotels.payAtHotel')}</span>
                        <span className="font-semibold">
                          {isConverting ? (
                            <Skeleton className="h-4 w-16 inline-block" />
                          ) : (
                            formatPrice(propertyTaxesTotal)
                          )}
                        </span>
                      </div>
                    </>
                  ) : (
                    <div className="flex justify-between text-lg font-semibold">
                      <span>{t('checkout.total')}:</span>
                      <span>
                        {isConverting ? (
                          <Skeleton className="h-6 w-24 inline-block" />
                        ) : (
                          formatPrice(total)
                        )}
                      </span>
                    </div>
                  )}
                </div>
                <Button 
                  className="w-full" 
                  size="lg" 
                  asChild
                  disabled={hasUnvalidatedItems}
                >
                  <Link to="/checkout">
                    {hasUnvalidatedItems ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        {t('checkout.validatingPrices')}
                      </>
                    ) : (
                      t('cart.goToCheckout')
                    )}
                  </Link>
                </Button>
                {hasUnvalidatedItems && (
                  <p className="text-xs text-muted-foreground text-center">
                    {t('checkout.validatingPrices')}
                  </p>
                )}
              </div>
            </>
          )}
        </div>
      </SheetContent>

      <ValidationChangesDialog
        open={changesDialogOpen}
        onOpenChange={setChangesDialogOpen}
        changes={parsedChanges}
        onConfirm={handleConfirmChanges}
        onRemove={handleRemoveChangedItem}
      />

      <CartExpirationDialog
        open={showExpirationDialog}
        onOpenChange={setShowExpirationDialog}
        expiredItems={expiredItems}
        onRevalidate={handleRevalidateExpired}
        onRemove={handleRemoveExpired}
      />
    </Sheet>
  );
};
